// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { GoogleMap, GeoJson } from '@realadvisor/google-maps';
import type { GoogleMapChildren } from '@realadvisor/google-maps';
import { useTheme } from '../../theme';
import { useUserData } from '../../hooks/user-data';
import type { GeogMap_geog$key } from './__generated__/GeogMap_geog.graphql';

type Props = {|
  geog: GeogMap_geog$key,
  children?: GoogleMapChildren,
|};

const MAP_PADDING = 20;

const getBBox2D = bbox => {
  if (bbox.length === 6) {
    const [swLng, swLat, , neLng, neLat] = bbox;
    return [swLng, swLat, neLng, neLat];
  }
  return bbox;
};

export const COUNTRY_BOUNDS = {
  CH: {
    ne: { lat: 48.783998564205845, lng: 11.003132474657832 },
    sw: { lat: 44.13365704557453, lng: 5.879898679491816 },
  },
  FR: {
    ne: { lat: 51.268318, lng: 9.8678344 },
    sw: { lat: 41.2632185, lng: -5.4534286 },
  },
  DE: {
    ne: { lat: 54.9716, lng: 15.419715 },
    sw: { lat: 47.25721, lng: 5.510048 },
  },
  ES: {
    ne: { lat: 43.7483377142, lng: 3.03948408368 },
    sw: { lat: 35.946850084, lng: -9.39288367353 },
  },
  GB: {
    ne: { lat: 58.6350001085, lng: 1.68153079591 },
    sw: { lat: 49.959999905, lng: -7.57216793459 },
  },
  IT: {
    ne: { lat: 47.1153931748, lng: 18.4802470232 },
    sw: { lat: 36.619987291, lng: 6.7499552751 },
  },
};

const convertBboxToNwSe = (bbox, countryCode) => {
  if (!bbox) {
    return COUNTRY_BOUNDS[countryCode];
  }

  const [swLng, swLat, neLng, neLat] = getBBox2D(bbox);
  if (swLng == null || swLat == null || neLng == null || neLat == null) {
    return COUNTRY_BOUNDS[countryCode];
  }

  return {
    ne: { lat: neLat, lng: neLng },
    sw: { lat: swLat, lng: swLng },
  };
};

export const GeogMap = (props: Props): React.Node => {
  const geog = useFragment(
    graphql`
      fragment GeogMap_geog on GeoJSONMultiPolygon {
        coordinates
        bbox
      }
    `,
    props.geog,
  );

  const { colors } = useTheme();
  const { countryCode } = useUserData();

  const geoJSONBGStyle = React.useMemo(
    () => ({
      fillColor: 'transparent',
      strokeColor: colors.white,
      strokeWeight: 4,
      strokeOpacity: 1,
      cursor: 'hand',
    }),
    [colors.white],
  );

  const geoJSONStyle = React.useMemo(
    () => ({
      fillColor: 'transparent',
      strokeColor: colors.primaryMain,
      strokeWeight: 2,
      strokeOpacity: 1,
      cursor: 'hand',
    }),
    [colors.primaryMain],
  );

  const geoJSONFeature = {
    type: 'Feature',
    geometry: {
      type: 'MultiPolygon',
      coordinates: geog.coordinates,
    },
  };

  return (
    <GoogleMap
      defaultBbox={convertBboxToNwSe(geog.bbox, countryCode)}
      defaultPadding={MAP_PADDING}
      mapTypeId={'roadmap'}
    >
      <GeoJson style={geoJSONBGStyle} data={geoJSONFeature} activeId={null} />
      <GeoJson style={geoJSONStyle} data={geoJSONFeature} activeId={null} />
      {props.children ?? null}
    </GoogleMap>
  );
};
